import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom"; 
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase/firebase";
import AuthFormCard from "../components/AuthFormCard";
import { login, loginWithGoogle, loginWithGithub } from "../services/auth";
import "./AuthPage.css";

const PUBLIC_URL = process.env.PUBLIC_URL || "";

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [socialError, setSocialError] = useState("");
  const [authChecked, setAuthChecked] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setAuthChecked(true);
      if (user) navigate("/", { replace: true });
    });
    return () => unsub();
  }, [navigate]);

  useEffect(() => {
    try {
      const redirectMessage = sessionStorage.getItem("authRedirectMessage");
      if (redirectMessage) {
        setSocialError(redirectMessage);
        sessionStorage.removeItem("authRedirectMessage");
      }
    } catch {
      // ignore storage errors
    }
  }, []);

  const handleGoogle = async () => {
    setSocialError("");
    try {
      await loginWithGoogle();
      navigate("/");
    } catch (err: unknown) {
      const code = err && typeof err === "object" && "code" in err ? (err as { code: string }).code : "";
      setSocialError(code === "auth/popup-closed-by-user" ? "" : "Sign in with Google failed. Please try again.");
    }
  };

  const handleGithub = async () => {
    setSocialError("");
    try {
      await loginWithGithub();
      navigate("/");
    } catch (err: unknown) {
      const code = err && typeof err === "object" && "code" in err ? (err as { code: string }).code : "";
      setSocialError(code === "auth/popup-closed-by-user" ? "" : "Sign in with GitHub failed. Please try again.");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError("");
    setPasswordError("");
    try {
      await login(email, password, remember);
      navigate("/");
    } catch (err: unknown) {
      const code = err && typeof err === "object" && "code" in err ? (err as { code: string }).code : "";
      if (code === "auth/user-not-found" || code === "auth/invalid-credential") {
        setEmailError("This email address isn't connected to an account.");
        setPasswordError("The password is incorrect.");
      } else if (code === "auth/invalid-email") {
        setEmailError("Please enter a valid email address.");
      } else {
        setPasswordError("Something went wrong. Please try again.");
      }
    }
  };

  if (!authChecked) return null;

  const canSubmit = !!email.trim() && !!password;

  const socialBlock = (
    <div className="auth-social">
      {socialError && <p className="auth-field-error" role="alert">{socialError}</p>}
      <button type="button" className="auth-social-btn" onClick={handleGoogle}>
        <img src={`${PUBLIC_URL}/Icon/Googlee.svg`} alt="" aria-hidden />
        Sign in with Google
      </button>
      <button type="button" className="auth-social-btn" onClick={handleGithub}>
        <img src={`${PUBLIC_URL}/image%201.svg`} alt="" aria-hidden />
        Sign in with GitHub
      </button>
    </div>
  );

  return (
    <AuthFormCard
      title="Sign In"
      subtitle="Welcome back! Please enter your details."
      primaryButtonText="Sign In"
      primaryDisabled={!canSubmit}
      bottomText="Don't have an account?"
      bottomLinkText="Sign Up"
      bottomLinkTo="/signup"
      onPrimarySubmit={handleSubmit}
      afterSubmit={socialBlock}
    >
      <div className="auth-field">
        <label htmlFor="login-email">Email</label>
        <div
          className={`auth-input-wrapper${emailError ? " auth-input-wrapper--error auth-input-wrapper--has-suffix" : ""}`}
        >
          <input
            id="login-email"
            type="email"
            className="auth-input"
            placeholder="Enter your email"
            autoComplete={remember ? "email" : "off"}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailError("");
            }}
          />
          {emailError && (
            <span className="auth-input-suffix">
              <img
                src={`${PUBLIC_URL}/icon-wrapper.svg`}
                alt=""
                aria-hidden
              />
            </span>
          )}
        </div>
        {emailError && (
          <p className="auth-field-error" role="alert">
            {emailError}
          </p>
        )}
      </div>
      <div className="auth-field">
        <label htmlFor="login-password">Password</label>
        <div
          className={`auth-input-wrapper${passwordError ? " auth-input-wrapper--error auth-input-wrapper--double-suffix" : " auth-input-wrapper--has-suffix"}`}
        >
          <input
            id="login-password"
            type={showPassword ? "text" : "password"}
            className="auth-input"
            placeholder="Enter your password"
            autoComplete={remember ? "current-password" : "off"}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setPasswordError("");
            }}
          />
          <span className="auth-input-suffix">
            <button
              type="button"
              className="auth-input-icon-btn"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <img
                src={`${PUBLIC_URL}/${showPassword ? "input-suffix.svg" : "Vector.svg"}`}
                alt=""
              />
            </button>
            {passwordError && (
              <img
                src={`${PUBLIC_URL}/icon-wrapper.svg`}
                alt=""
                aria-hidden
              />
            )}
          </span>
        </div>
        {passwordError && (
          <p className="auth-field-error" role="alert">
            {passwordError}
          </p>
        )}
      </div>
      <div className="auth-options">
        <label className="auth-remember">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
          />
          <span className="auth-toggle" aria-hidden="true" />
          Remember me
        </label>
        <Link to="/forgot-password" className="auth-link">
          Forgot password
        </Link>
      </div>
    </AuthFormCard>
  );
};

export default LoginPage;
